/**
 * AKY CRO — core runtime.
 *
 * Loaded once, before every component script. Exposes a single global,
 * `window.Theme`, so component files never leak names of their own.
 *
 * Component scripts depend on this file being parsed first; `defer` on every
 * script tag guarantees that execution order.
 */
(function () {
  'use strict';

  const root = document.documentElement;
  const config = JSON.parse(document.getElementById('aky-config')?.textContent || '{}');

  /* ------------------------------------------------------------------ */
  /* Utilities                                                           */
  /* ------------------------------------------------------------------ */

  const utils = {
    debounce(fn, wait = 200) {
      let timer;
      return function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), wait);
      };
    },

    throttle(fn, limit = 100) {
      let waiting = false;
      return function (...args) {
        if (waiting) return;
        fn.apply(this, args);
        waiting = true;
        setTimeout(() => {
          waiting = false;
        }, limit);
      };
    },

    /**
     * Formats a price in cents using the shop's money format string.
     * Mirrors Shopify's Liquid `money` filter so client-rendered prices match
     * server-rendered ones exactly.
     */
    formatMoney(cents, format) {
      if (typeof cents === 'string') cents = cents.replace('.', '');
      const formatString = format || config.moneyFormat || '{{ amount }}';
      const placeholder = /\{\{\s*(\w+)\s*\}\}/;

      function withDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
        if (isNaN(number) || number === null) return '0';
        const fixed = (number / 100.0).toFixed(precision);
        const parts = fixed.split('.');
        const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
        const cts = parts[1] ? decimal + parts[1] : '';
        return dollars + cts;
      }

      const match = formatString.match(placeholder);
      if (!match) return formatString;

      let value = '';
      switch (match[1]) {
        case 'amount':
          value = withDelimiters(cents, 2, ',', '.');
          break;
        case 'amount_no_decimals':
          value = withDelimiters(cents, 0, ',', '.');
          break;
        case 'amount_with_comma_separator':
          value = withDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = withDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_with_space_separator':
          value = withDelimiters(cents, 2, ' ', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = withDelimiters(cents, 0, ' ', ',');
          break;
        case 'amount_with_apostrophe_separator':
          value = withDelimiters(cents, 2, "'", '.');
          break;
        default:
          value = withDelimiters(cents, 2, ',', '.');
      }

      return formatString.replace(placeholder, value);
    },

    /** Standard options for every Shopify AJAX Cart API call. */
    fetchConfig(type = 'json', body) {
      const options = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: `application/${type}`,
        },
      };
      if (body !== undefined) options.body = JSON.stringify(body);
      return options;
    },

    /** Parses a `<section>` HTML string and returns one element from it. */
    parseSection(html, selector) {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    },

    /**
     * 'smooth' or 'auto', depending on the visitor's motion preference.
     *
     * The CSS `prefers-reduced-motion` block sets scroll-behavior: auto, but a
     * behavior passed explicitly to scrollTo/scrollIntoView in JavaScript
     * overrides the stylesheet — so every scripted scroll has to ask.
     */
    scrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    },

    getFocusableElements(container) {
      return Array.from(
        container.querySelectorAll(
          'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe'
        )
      );
    },
  };

  /* ------------------------------------------------------------------ */
  /* Pub/sub                                                             */
  /* ------------------------------------------------------------------ */

  const subscribers = {};

  const pubsub = {
    EVENTS: {
      cartAdd: 'cart:add',
      cartUpdate: 'cart:updated',
      cartError: 'cart:error',
      variantChange: 'variant:change',
      quantityChange: 'quantity:change',
      productUpdate: 'product:updated',
    },

    subscribe(event, callback) {
      (subscribers[event] = subscribers[event] || []).push(callback);
      return function unsubscribe() {
        subscribers[event] = subscribers[event].filter((cb) => cb !== callback);
      };
    },

    /**
     * Notifies internal subscribers and also dispatches a real CustomEvent on
     * `document`, so Shopify apps and any third-party script can hook into the
     * same signals without depending on this object.
     */
    publish(event, data) {
      (subscribers[event] || []).forEach((cb) => cb(data));
      document.dispatchEvent(new CustomEvent(event, { detail: data, bubbles: true }));
    },
  };

  /* ------------------------------------------------------------------ */
  /* Accessibility                                                       */
  /* ------------------------------------------------------------------ */

  let trapState = null;
  let scrollPosition = 0;

  const a11y = {
    /**
     * Keeps Tab inside `container` until the trap is removed.
     *
     * The focusable list is read on every Tab rather than captured once. The
     * cart drawer re-renders its contents after each cart change, and the
     * filter drawer after each filter, so a list captured at open time would
     * be pointing at detached nodes minutes later — and a trap whose "last
     * element" no longer exists silently lets Tab walk out into the page
     * behind it. Recomputing costs one querySelectorAll per keypress.
     */
    trapFocus(container, elementToFocus = container) {
      this.removeTrapFocus();

      function handleKeydown(event) {
        if (event.key !== 'Tab') return;

        const focusable = utils.getFocusableElements(container).filter((el) => {
          // Skip anything hidden: a collapsed <details>, a [hidden] panel, or
          // a zero-size control is not somewhere Tab should land.
          if (el.hasAttribute('disabled') || el.closest('[hidden]')) return false;
          return el.getClientRects().length > 0;
        });

        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;

        // Focus has drifted outside the container entirely — pull it back.
        if (!container.contains(active)) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
          return;
        }

        if (event.shiftKey && active === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && active === last) {
          event.preventDefault();
          first.focus();
        }
      }

      trapState = { container, handleKeydown, previouslyFocused: document.activeElement };
      document.addEventListener('keydown', handleKeydown);

      elementToFocus.setAttribute('tabindex', '-1');
      elementToFocus.focus({ preventScroll: true });
    },

    removeTrapFocus(returnFocus = false) {
      if (!trapState) return;
      document.removeEventListener('keydown', trapState.handleKeydown);
      if (returnFocus && trapState.previouslyFocused instanceof HTMLElement) {
        trapState.previouslyFocused.focus({ preventScroll: true });
      }
      trapState = null;
    },

    /**
     * Locks page scroll without the iOS "jump to top" bug: the body is pinned
     * at its current offset rather than simply set to overflow:hidden.
     */
    lockScroll() {
      if (document.body.dataset.scrollLocked === 'true') return;
      scrollPosition = window.scrollY;
      document.body.style.top = `-${scrollPosition}px`;
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.dataset.scrollLocked = 'true';
    },

    unlockScroll() {
      if (document.body.dataset.scrollLocked !== 'true') return;
      document.body.style.removeProperty('top');
      document.body.style.removeProperty('position');
      document.body.style.removeProperty('width');
      delete document.body.dataset.scrollLocked;
      window.scrollTo(0, scrollPosition);
    },
  };

  /* ------------------------------------------------------------------ */
  /* Cart                                                                */
  /* ------------------------------------------------------------------ */

  const cart = {
    /**
     * Section IDs that should be re-rendered after every cart mutation.
     * Components register themselves here so the cart never guesses.
     */
    sectionsToRender: new Set(),

    registerSection(id) {
      if (id) this.sectionsToRender.add(id);
    },

    /**
     * @param {string} url      one of the AJAX Cart API endpoints
     * @param {object} body     endpoint payload
     * @param {string} kind     'add' or 'update' — /cart/add.js and
     *                          /cart/change.js both return an object carrying
     *                          `items`, so the response shape cannot tell them
     *                          apart. The caller states which it was, and only
     *                          an 'add' opens the drawer.
     */
    async request(url, body, kind) {
      const payload = { ...body };
      if (this.sectionsToRender.size) {
        payload.sections = Array.from(this.sectionsToRender);
        payload.sections_url = window.location.pathname;
      }

      const response = await fetch(url, utils.fetchConfig('json', payload));
      const data = await response.json();

      if (!response.ok || data.status) {
        const error = new Error(data.description || data.message || 'Cart request failed');
        error.data = data;
        pubsub.publish(pubsub.EVENTS.cartError, data);
        throw error;
      }

      // /cart/change.js and /cart/update.js return the whole cart;
      // /cart/add.js returns only the lines that were added. Rather than
      // spending an extra /cart.js round trip on every add, subscribers read
      // totals from the re-rendered sections, which Shopify built from the
      // same request.
      const cartState = kind === 'update' ? data : null;
      if (cartState) this.state = cartState;

      if (kind === 'add') pubsub.publish(pubsub.EVENTS.cartAdd, { response: data, sections: data.sections });
      pubsub.publish(pubsub.EVENTS.cartUpdate, { cart: cartState, sections: data.sections });

      return data;
    },

    add(items) {
      return this.request(
        config.routes.cartAdd,
        { items: Array.isArray(items) ? items : [items] },
        'add'
      );
    },

    /** `line` is the 1-based line index; `id` is the line item key. */
    change({ line, id, quantity, properties }) {
      const body = { quantity };
      if (line !== undefined) body.line = line;
      if (id !== undefined) body.id = id;
      if (properties !== undefined) body.properties = properties;
      return this.request(config.routes.cartChange, body, 'update');
    },

    update(updates) {
      return this.request(config.routes.cartUpdate, { updates }, 'update');
    },

    async get() {
      const response = await fetch(`${config.routes.cart}.js`);
      return response.json();
    },
  };

  /* ------------------------------------------------------------------ */
  /* Modals                                                              */
  /* ------------------------------------------------------------------ */

  /**
   * Any element with [data-modal] becomes a dialog. Opened by
   * [data-modal-open="<id>"], closed by [data-modal-close], the overlay, or
   * Escape. Delegated, so markup can be re-rendered freely.
   */
  const modal = {
    open(id) {
      const target = document.getElementById(id);
      if (!target || !target.hasAttribute('data-modal')) return;

      target.hidden = false;
      // Layout pass first so the transition animates; set synchronously
      // because requestAnimationFrame does not fire in a background tab.
      void target.offsetWidth;
      target.dataset.open = 'true';
      a11y.lockScroll();
      a11y.trapFocus(target, target.querySelector('[data-modal-close]') || target);
      this.current = target;
    },

    close() {
      const target = this.current;
      if (!target) return;

      target.dataset.open = 'false';
      this.current = null;
      a11y.removeTrapFocus(true);
      a11y.unlockScroll();

      const finish = () => {
        if (target.dataset.open !== 'true') target.hidden = true;
      };
      target.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, 400);
    },
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-modal-open]');
    if (opener) {
      event.preventDefault();
      modal.open(opener.dataset.modalOpen);
      return;
    }

    if (event.target.closest('[data-modal-close]')) {
      event.preventDefault();
      modal.close();
      return;
    }

    // Clicking the backdrop (the modal element itself, not its panel).
    const dialog = event.target.closest('[data-modal]');
    if (dialog && event.target === dialog) modal.close();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && modal.current) modal.close();
  });

  /* ------------------------------------------------------------------ */
  /* Public API                                                          */
  /* ------------------------------------------------------------------ */

  window.Theme = {
    config,
    utils,
    pubsub,
    a11y,
    cart,
    modal,
    strings: config.strings || {},
    /** True while the merchant is editing the theme in the admin. */
    get designMode() {
      return Boolean(window.Shopify && window.Shopify.designMode);
    },
  };

  root.classList.replace('no-js', 'js');
})();
